import React from 'react';
import { 
  TrophyIcon, 
  HeartIcon, 
  LightBulbIcon, 
  UserGroupIcon,
  MapIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';

const AboutSection: React.FC = () => {
  const features = [
    {
      icon: TrophyIcon,
      title: 'Excellence in Sports',
      description: 'World-class training facilities and coaching methodology to nurture champions.'
    },
    {
      icon: MapIcon,
      title: 'Statewide Coverage',
      description: 'Reaching every corner of Odisha across all 314 blocks for inclusive development.'
    },
    {
      icon: AcademicCapIcon,
      title: 'Professional Coaching',
      description: 'Certified coaches with international standards and proven track records.'
    },
    {
      icon: UserGroupIcon,
      title: 'Community Focus',
      description: 'Building strong sporting communities that support and nurture talent together.'
    }
  ];

  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-slide-up">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            About <span className="text-primary-600">Kreed Odisha</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-600 to-success-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            A landmark initiative by Hardini Dynamics to revolutionize sports culture 
            across Odisha and create a pipeline of world-class athletes.
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Left Content */}
          <div className="space-y-8 animate-slide-up">
            <div className="space-y-6">
              <div className="inline-flex items-center space-x-3 bg-primary-100 rounded-full px-4 py-2">
                <HeartIcon className="w-5 h-5 text-primary-600" />
                <span className="text-primary-700 font-semibold">Our Mission</span>
              </div>
              
              <h3 className="text-3xl font-bold text-gray-900 leading-tight">
                Transforming Odisha into India's Sporting Powerhouse
              </h3>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Kreed Odisha is more than just a sports program—it's a movement to identify, 
                nurture, and develop sporting talent from every village and block across the state. 
                We believe that every child deserves access to quality sports training and the 
                opportunity to excel on national and international platforms.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Through our comprehensive approach, we're building a sustainable ecosystem that 
                combines grassroots development with elite performance training, ensuring that 
                Odisha's sporting future is bright and inclusive.
              </p>
            </div>

            {/* Key Numbers */}
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="text-3xl font-bold text-primary-600 mb-2">314</div>
                <div className="text-gray-600">Blocks Covered</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="text-3xl font-bold text-success-600 mb-2">15+</div>
                <div className="text-gray-600">Sports Disciplines</div>
              </div>
            </div>
          </div>

          {/* Right Content - Image/Visual */}
          <div className="relative animate-slide-up">
            <div className="relative bg-gradient-to-br from-primary-600 to-success-600 rounded-2xl p-8 shadow-2xl">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-white">
                <LightBulbIcon className="w-16 h-16 mb-6 text-warning-400" />
                <h4 className="text-2xl font-bold mb-4">Our Vision</h4>
                <p className="text-lg leading-relaxed opacity-90">
                  To establish Odisha as a national sporting powerhouse, nurturing talent 
                  from every village and creating champions who bring pride to our state 
                  and country on the global stage.
                </p>
              </div>
              
              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-warning-400 rounded-full opacity-20"></div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-success-400 rounded-full opacity-20"></div>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="group bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 animate-slide-up"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-primary-600 to-success-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                {feature.title}
              </h4>
              <p className="text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16 animate-slide-up">
          <div className="bg-gradient-to-r from-primary-600 to-success-600 rounded-2xl p-8 text-white">
            <h3 className="text-3xl font-bold mb-4">Ready to Join the Movement?</h3>
            <p className="text-xl mb-6 opacity-90">
              Be part of Odisha's sporting revolution. Apply now and take the first step 
              towards your athletic dreams.
            </p>
            <button
              onClick={() => window.location.href = '/apply'}
              className="bg-white text-primary-600 font-bold px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors transform hover:scale-105 shadow-lg"
            >
              Start Your Application
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;